import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { all_routes } from "../../../Router/all_routes";
import axios from "axios";
import { base_api_url } from "../../../environment.jsx";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { QrCode, KeyRound, ArrowLeft, CheckCircle } from "lucide-react";
import "./resetpassword.css";

const MySwal = withReactContent(Swal);

const Resetpassword = () => {
  const route = all_routes;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Récupération du token depuis l'URL
  const token = searchParams.get("token") || "";
  const emailFromUrl = searchParams.get("email") || "";

  const [formData, setFormData] = useState({
    mot_de_passe: "",
    repassword: "",
  });

  const [visibility, setVisibility] = useState({
    mot_de_passe: false,
    repassword: false,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleVisibility = (field) => {
    setVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Calcul de la force du mot de passe
  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, label: "", color: "" };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    const map = [
      { level: 1, label: "Très faible", color: "#ef4444" },
      { level: 2, label: "Faible", color: "#f97316" },
      { level: 3, label: "Moyen", color: "#eab308" },
      { level: 4, label: "Fort", color: "#22c55e" },
    ];
    return map[score - 1] || { level: 0, label: "", color: "" };
  };

  const strength = getPasswordStrength(formData.mot_de_passe);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.mot_de_passe.length < 8) {
      MySwal.fire({
        icon: "warning",
        text: "Le mot de passe doit comporter au moins 8 caractères.",
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "pixel-toast" },
      });
      return;
    }

    if (formData.mot_de_passe !== formData.repassword) {
      MySwal.fire({
        icon: "error",
        text: "Les mots de passe ne correspondent pas.",
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "pixel-toast" },
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${base_api_url}api/reset-password`,
        { mot_de_passe: formData.mot_de_passe, token },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Succès : afficher le message backend puis rediriger
      const successMsg = response.data?.message || "Mot de passe réinitialisé avec succès";
      MySwal.fire({
        icon: "success",
        text: successMsg,
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "pixel-toast" },
      });

      setSuccess(true);
      setTimeout(() => navigate(route.signin), 2500);
    } catch (error) {
      let errorMessage = "Une erreur est survenue. Veuillez réessayer.";
      if (error.response?.data?.msg) {
        errorMessage = error.response.data.msg;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 422 || error.response?.status === 400) {
        errorMessage = "Le lien de réinitialisation est invalide ou a expiré.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.request) {
        errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion.";
      }

      MySwal.fire({
        icon: "error",
        text: errorMessage,
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
        customClass: { popup: "pixel-toast" },
      });
    } finally {
      setLoading(false);
    }
  };

  // Icônes œil SVG
  const EyeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  const EyeOffIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </svg>
  );

  return (
    <div className="rp-container">
      {/* Section Gauche : Branding */}
      <div className="rp-left-section">
        <div className="rp-blur-effect"></div>

        <div className="rp-left-content">
          <div className="rp-logo-wrapper" onClick={() => navigate("/")}>
            <div className="rp-logo-icon">
              <QrCode />
            </div>
            <span className="rp-logo-text">
              QR<span className="rp-logo-text-accent">NYAMA</span>
            </span>
          </div>
          <h2 className="rp-main-title">
            Nouveau <br />
            <span className="rp-title-accent">mot de passe.</span>
          </h2>
          <p className="rp-left-subtitle">
            Choisissez un mot de passe sécurisé d&apos;au moins 8 caractères
            pour protéger votre compte.
          </p>
        </div>

        <p className="rp-copyright">© 2026 QRNYAMA. Tous droits réservés.</p>
      </div>

      {/* Section Droite */}
      <div className="rp-right-section">
        <div className="rp-form-container">
          {/* Logo mobile */}
          <div className="rp-mobile-logo">
            <div className="rp-mobile-logo-content">
              <div className="rp-mobile-logo-icon">
                <QrCode />
              </div>
              <span className="rp-mobile-logo-text">
                QR<span className="rp-logo-text-accent">NYAMA</span>
              </span>
            </div>
          </div>

          {!success ? (
            <>
              {/* En-tête */}
              <div className="rp-header">
                <div className="rp-icon-wrapper">
                  <KeyRound size={28} />
                </div>
                <h3 className="rp-header-title">Réinitialiser le mot de passe</h3>
                <p className="rp-header-subtitle">
                  {emailFromUrl
                    ? `Créez un nouveau mot de passe pour ${emailFromUrl}.`
                    : "Créez un nouveau mot de passe pour votre compte."}
                </p>
              </div>

              {/* Formulaire */}
              <form onSubmit={handleSubmit} className="rp-form">
                {/* Nouveau mot de passe */}
                <div className="rp-form-group">
                  <label className="rp-label">Nouveau mot de passe</label>
                  <div className="rp-password-wrapper">
                    <input
                      type={visibility.mot_de_passe ? "text" : "password"}
                      name="mot_de_passe"
                      value={formData.mot_de_passe}
                      onChange={handleChange}
                      required
                      placeholder="Minimum 8 caractères"
                      className="rp-input"
                    />
                    <button
                      type="button"
                      onClick={() => toggleVisibility("mot_de_passe")}
                      className="rp-password-toggle"
                    >
                      {visibility.mot_de_passe ? <EyeIcon /> : <EyeOffIcon />}
                    </button>
                  </div>

                  {/* Indicateur de force */}
                  {formData.mot_de_passe && (
                    <div className="rp-strength">
                      <div className="rp-strength-bars">
                        {[1, 2, 3, 4].map((i) => (
                          <div
                            key={i}
                            className="rp-strength-bar"
                            style={{
                              backgroundColor:
                                i <= strength.level ? strength.color : "#e7e5e4",
                            }}
                          />
                        ))}
                      </div>
                      <span
                        className="rp-strength-label"
                        style={{ color: strength.color }}
                      >
                        {strength.label}
                      </span>
                    </div>
                  )}
                </div>

                {/* Confirmation */}
                <div className="rp-form-group">
                  <label className="rp-label">Confirmer le mot de passe</label>
                  <div className="rp-password-wrapper">
                    <input
                      type={visibility.repassword ? "text" : "password"}
                      name="repassword"
                      value={formData.repassword}
                      onChange={handleChange}
                      required
                      placeholder="Répétez votre mot de passe"
                      className={`rp-input ${
                        formData.repassword &&
                        formData.mot_de_passe !== formData.repassword
                          ? "rp-input-error"
                          : ""
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => toggleVisibility("repassword")}
                      className="rp-password-toggle"
                    >
                      {visibility.repassword ? <EyeIcon /> : <EyeOffIcon />}
                    </button>
                  </div>
                  {formData.repassword &&
                    formData.mot_de_passe !== formData.repassword && (
                      <p className="rp-mismatch">
                        Les mots de passe ne correspondent pas.
                      </p>
                    )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="rp-submit-btn"
                >
                  {loading ? (
                    <span className="rp-btn-loading">
                      <span className="rp-spinner"></span>
                      Mise à jour en cours...
                    </span>
                  ) : (
                    "Définir le nouveau mot de passe"
                  )}
                </button>
              </form>

              <p className="rp-footer-text">
                <Link to={route.signin} className="rp-back-link">
                  <ArrowLeft size={14} />
                  Retour à la connexion
                </Link>
              </p>
            </>
          ) : (
            /* État succès */
            <div className="rp-success-state">
              <div className="rp-success-icon">
                <CheckCircle size={40} />
              </div>
              <h3 className="rp-success-title">Mot de passe mis à jour !</h3>
              <p className="rp-success-message">
                Votre mot de passe a été réinitialisé avec succès. Vous pouvez
                maintenant vous connecter avec votre nouveau mot de passe.
              </p>
              <button
                onClick={() => navigate(route.signin)}
                className="rp-submit-btn"
              >
                Se connecter
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Resetpassword;
